import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { http, qs } from "../client";
import { keys } from "../keys";
import type {
  AddInstructorInput,
  CourseDetail,
  CourseInput,
  CourseInstructor,
  CourseSummary,
  CourseUpdateInput,
  InboxQuestion,
  LearnerAccessInput,
  JoinCourseInput,
  JoinPreview,
  Ok,
  RosterStudent,
  RosterUpdateInput,
} from "../types";

export function useCourses({ archived = false }: { archived?: boolean } = {}) {
  return useQuery({
    queryKey: keys.courses.list(archived),
    queryFn: ({ signal }) => http.get<CourseSummary[]>(`/courses${qs({ archived: archived || undefined })}`, signal),
  });
}

export function useCourse(courseId: number) {
  return useQuery({
    queryKey: keys.courses.detail(courseId),
    queryFn: ({ signal }) => http.get<CourseDetail>(`/courses/${courseId}`, signal),
    enabled: Number.isFinite(courseId),
  });
}

function useInvalidateCourses() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.courses.all }),
      queryClient.invalidateQueries({ queryKey: keys.dashboard.student }),
      queryClient.invalidateQueries({ queryKey: keys.dashboard.instructor }),
    ]);
}

export function useCreateCourse() {
  const invalidate = useInvalidateCourses();
  return useMutation({
    mutationFn: (input: CourseInput) => http.post<CourseDetail>("/courses", input),
    onSuccess: invalidate,
  });
}

export function useUpdateCourse(courseId: number) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCourses();
  return useMutation({
    mutationFn: (input: CourseUpdateInput) => http.patch<CourseDetail>(`/courses/${courseId}`, input),
    onSuccess: async (course) => {
      queryClient.setQueryData(keys.courses.detail(courseId), course);
      await invalidate();
    },
  });
}

export function useDeleteCourse() {
  const invalidate = useInvalidateCourses();
  return useMutation({
    mutationFn: (courseId: number) => http.delete(`/courses/${courseId}`),
    onSuccess: invalidate,
  });
}

export function useRegenerateJoinCode(courseId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<CourseDetail>(`/courses/${courseId}/join-code`),
    onSuccess: (course) => queryClient.setQueryData(keys.courses.detail(courseId), course),
  });
}

export function useJoinCourse() {
  const invalidate = useInvalidateCourses();
  return useMutation({
    mutationFn: (input: JoinCourseInput) => http.post<CourseSummary>("/courses/join", input),
    onSuccess: invalidate,
  });
}

export function useLeaveCourse() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCourses();
  return useMutation({
    mutationFn: (courseId: number) => http.post<Ok>(`/courses/${courseId}/leave`),
    onSuccess: (_, courseId) => {
      // The course is no longer visible to this learner: drop its cached data instead of refetching a 404.
      queryClient.removeQueries({ queryKey: keys.courses.detail(courseId) });
      queryClient.removeQueries({ queryKey: keys.assignments.list(courseId) });
      return invalidate();
    },
  });
}

export function useJoinPreview(code: string) {
  return useQuery({
    queryKey: keys.courses.joinPreview(code),
    queryFn: ({ signal }) => http.get<JoinPreview>(`/join/${encodeURIComponent(code)}`, signal),
    enabled: code.length > 0,
    retry: false,
  });
}

export function useRoster(courseId: number, section?: string | null) {
  return useQuery({
    queryKey: keys.courses.roster(courseId, section),
    queryFn: ({ signal }) => http.get<RosterStudent[]>(`/courses/${courseId}/students${qs({ section })}`, signal),
  });
}

export function useUpdateRosterStudent(courseId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: RosterUpdateInput & { userId: number }) =>
      http.patch<RosterStudent>(`/courses/${courseId}/students/${userId}`, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["courses", "roster", courseId] }),
        queryClient.invalidateQueries({ queryKey: keys.courses.detail(courseId) }),
        queryClient.invalidateQueries({ queryKey: keys.analytics.all }),
      ]),
  });
}

export function useSetLearnerAccess(courseId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: LearnerAccessInput & { userId: number }) =>
      http.put<RosterStudent>(`/courses/${courseId}/students/${userId}/access`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["courses", "roster", courseId] }),
  });
}

export function useRemoveStudent(courseId: number) {
  const invalidate = useInvalidateCourses();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => http.delete(`/courses/${courseId}/students/${userId}`),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: keys.analytics.all });
    },
  });
}

export function useInstructors(courseId: number) {
  return useQuery({
    queryKey: keys.courses.instructors(courseId),
    queryFn: ({ signal }) => http.get<CourseInstructor[]>(`/courses/${courseId}/instructors`, signal),
  });
}

export function useAddInstructor(courseId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddInstructorInput) => http.post<CourseInstructor[]>(`/courses/${courseId}/instructors`, input),
    onSuccess: (list) => {
      queryClient.setQueryData(keys.courses.instructors(courseId), list);
      return queryClient.invalidateQueries({ queryKey: keys.courses.detail(courseId) });
    },
  });
}

export function useRemoveInstructor(courseId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => http.delete(`/courses/${courseId}/instructors/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.courses.all }),
  });
}

export type QuestionStatus = "open" | "resolved" | "all";

export function useQuestionsInbox(courseId: number, status: QuestionStatus = "open") {
  return useQuery({
    queryKey: keys.courses.questions(courseId, status),
    queryFn: ({ signal }) => http.get<InboxQuestion[]>(`/courses/${courseId}/questions${qs({ status })}`, signal),
  });
}
